'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  formatClassNames,
  isEmptyObject,
} from '@wix/editor-elements-common-utils';
import Image from '@wix/thunderbolt-elements/src/components/Image/viewer/Image';
import Link from '@wix/thunderbolt-elements/src/components/Link/viewer/Link';
import type { IPaginatedGridGalleryItemProps } from '../PaginatedGridGalleryItem.types';
import ItemClickActionWrapper from '../../commons/viewer/ItemClickActionWrapper/ItemClickActionWrapper';
import { TestIds } from '../../commons/constants';
import { Panel } from '../../PaginatedGridGallery/viewer/Panel';
import semanticClassNames from '../../PaginatedGridGallery/WIP_PaginatedGridGallery.semanticClassNames';

const PaginatedGridGalleryItem: React.FC<
  IPaginatedGridGalleryItemProps
> = props => {
  const {
    dataId,
    title,
    description,
    goToLinkText,
    link,
    image,
    itemId,
    skinsStyle,
    extraClassNames,
    imageOnClickAction,
    openImageZoom = () => {},
    itemClickHandler = () => {},
    translations,
    onItemMouseIn,
    onFocus,
    onBlur,
    hasPanel,
    extraStyle,
  } = props;

  const openImageZoomCallback = React.useCallback(
    () => openImageZoom(dataId),
    [dataId, openImageZoom],
  );

  const itemRef = React.useRef<HTMLDivElement>(null);

  const focusItemRoot = () => {
    if (itemRef.current) {
      itemRef.current.focus();
    }
  };

  return (
    <div
      className={classNames(
        skinsStyle.root,
        extraClassNames.root,
        formatClassNames(semanticClassNames.galleryItem),
      )}
      onMouseEnter={onItemMouseIn}
      onClick={itemClickHandler}
      tabIndex={-1}
      ref={itemRef}
      style={extraStyle}
    >
      <ItemClickActionWrapper
        imageOnClickAction={imageOnClickAction}
        openImageZoom={openImageZoomCallback}
        link={link}
        imgTitle={image.title || ''}
        imgAlt={image.alt}
        itemId={itemId}
        skinsStyle={skinsStyle}
        translations={translations}
        onFocus={onFocus}
        onBlur={onBlur}
        focusItemRoot={focusItemRoot}
      >
        <div className={skinsStyle.imageWrapper}>
          <div className={skinsStyle.imageBorder}>
            <Image
              className={skinsStyle.image}
              id={`img_${image.containerId}`}
              {...image}
              alt={image.alt || (image.title as string)}
            />
            <div data-testid={TestIds.panel} className={skinsStyle.panel}>
              <div className={skinsStyle.panelWrap}>
                {hasPanel && (
                  <Panel
                    title={title}
                    description={description}
                    skinsStyle={{
                      title: skinsStyle.title,
                      description: skinsStyle.description,
                    }}
                  />
                )}
                {imageOnClickAction !== 'goToLink' && !isEmptyObject(link) && (
                  <Link
                    className={skinsStyle.link}
                    dataTestId={TestIds.panelLink}
                    {...link}
                  >
                    {goToLinkText}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </ItemClickActionWrapper>
    </div>
  );
};

export default PaginatedGridGalleryItem;
